import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

// Click-to-zoom lightbox for product images. Supports multiple images with arrows.
export default function ImageLightbox({ open, images = [], startIndex = 0, title = "", onClose }) {
    const [idx, setIdx] = useState(startIndex);

    useEffect(() => { if (open) setIdx(startIndex); }, [open, startIndex]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
            if (e.key === "ArrowRight") setIdx((i) => (i + 1) % images.length);
            if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + images.length) % images.length);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, images.length, onClose]);

    if (!open || images.length === 0) return null;

    return (
        <div
            data-testid="image-lightbox"
            onClick={onClose}
            className="fixed inset-0 z-[70] flex flex-col items-center justify-center bg-slate-950/90 backdrop-blur-sm p-4 sm:p-8 animate-in fade-in duration-150"
        >
            <button
                type="button"
                data-testid="image-lightbox-close"
                onClick={onClose}
                className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors"
                title="关闭 (Esc)"
            >
                <X className="h-5 w-5" />
            </button>

            <img
                src={images[idx]}
                alt={title}
                data-testid="image-lightbox-image"
                onClick={(e) => e.stopPropagation()}
                className="max-h-[80vh] max-w-full rounded-lg object-contain shadow-2xl"
            />

            <div className="mt-4 flex items-center gap-4 text-white" onClick={(e) => e.stopPropagation()}>
                {images.length > 1 && (
                    <button
                        type="button"
                        data-testid="image-lightbox-prev"
                        onClick={() => setIdx((i) => (i - 1 + images.length) % images.length)}
                        className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                        title="上一张"
                    >
                        <ChevronLeft className="h-5 w-5" />
                    </button>
                )}
                <div className="text-center">
                    {title && <div className="text-sm font-medium">{title}</div>}
                    {images.length > 1 && (
                        <div className="text-xs text-white/60 font-mono mt-0.5">{idx + 1} / {images.length}</div>
                    )}
                </div>
                {images.length > 1 && (
                    <button
                        type="button"
                        data-testid="image-lightbox-next"
                        onClick={() => setIdx((i) => (i + 1) % images.length)}
                        className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                        title="下一张"
                    >
                        <ChevronRight className="h-5 w-5" />
                    </button>
                )}
            </div>
        </div>
    );
}
